import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
    FileText,
    Search,
    ArrowRight,
    AlertCircle,
    Layers,
    ExternalLink,
    Info,
} from "lucide-react";
import { api, formatApiError, API, tokenStore } from "../lib/api";
import ProjectHeader from "../components/ProjectHeader";
import ScriptLockedBanner from "../components/ScriptLockedBanner";
import MobilePdfHandoffCard from "../components/MobilePdfHandoffCard";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useRevalidateOnFocus } from "../lib/useRevalidateOnFocus";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";
import { computeScriptEmptyStateLink } from "../lib/scriptEmptyState";
// Iter-158.13.2.20f.34 — H&M AI Breakdown V1 (mock mode). Mounts
// directly beneath the Script PDF; composed gate below.
import HMBreakdownPanel from "../components/HMBreakdownPanel";

/**
 * iter-54 Script viewer — simplified and trustworthy.
 *
 * Principles (per user spec):
 *   1. The script is the source of truth — render the ORIGINAL uploaded
 *      PDF as-is. No reformatting, no extracted-text rendering.
 *   2. PDF viewing uses the browser's native viewer in an iframe. We do
 *      NOT try to mimic a PDF reader — we just give the browser the file
 *      and stay out of the way.
 *   3. Search is intentionally simple:
 *        - Scene number patterns (25, 25A, 1.4) → jump to the page we
 *          mapped during parse (best-effort).
 *        - Anything else (character names, free text) → users use the
 *          browser/PDF viewer's native Find (Cmd/Ctrl+F).
 *      We do NOT try to overlay our own find — that's what react-pdf
 *      would be for, and we're keeping this lightweight.
 *   4. For non-PDF source files (DOCX/TXT/Fountain/paste) we surface an
 *      "Open in new tab" CTA — these formats can't be rendered as a
 *      reliable script. The user keeps the original file, no fakes.
 *   5. Episodic TV: a tab strip at the top lets users switch episodes.
 */

function buildFileUrl(apiBase, projectId, scriptId, page, nonce) {
    const token = tokenStore.get() || "";
    const base = `${API}${apiBase}/${projectId}/scripts/${scriptId}/file`;
    const qs = new URLSearchParams();
    if (token) qs.set("token", token);
    if (nonce && nonce > 0) qs.set("_n", String(nonce));
    // Always force page-width zoom so the PDF fits the viewport on mobile.
    // Chromium + Firefox + iOS Safari all honour the fragment params:
    // #page=N&zoom=page-width&view=FitH
    const fragParts = ["zoom=page-width", "view=FitH"];
    if (page && page > 0) fragParts.unshift(`page=${page}`);
    return `${base}?${qs.toString()}#${fragParts.join("&")}`;
}

function parseSceneRef(query) {
    const trimmed = (query || "").trim();
    if (!trimmed) return null;
    // iter-55: be forgiving with how users type scene refs.
    //   "scene 25" / "Sc 25" / "Sc. 25" / "25" / "25A" → bare scene
    //   "1.4" / "1·4" / "1-4" → episode "1", scene "4"
    const cleaned = trimmed.replace(/^(?:sc(?:ene)?\.?\s*)/i, "").trim();
    const dotMatch = cleaned.match(/^(\w+)[.·\-](\d+[A-Z]*)$/i);
    if (dotMatch) {
        return { episode: dotMatch[1], sceneNumber: dotMatch[2].toUpperCase() };
    }
    if (/^\d+[A-Z]*$/i.test(cleaned)) {
        return { episode: null, sceneNumber: cleaned.toUpperCase() };
    }
    return null;
}

export default function ScriptPage() {
    const { projectId } = useParams();
    const routeBase = useRouteBase();
    const apiBase = useApiBase();

    const [project, setProject] = useState(null);
    const [scripts, setScripts] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [scenes, setScenes] = useState([]); // scenes for active episode
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [query, setQuery] = useState("");
    const [pdfPage, setPdfPage] = useState(0); // 0 = no jump
    // iter-63 PART 12: bumped on every "Go" press so the iframe reloads
    // even when the user re-jumps to the same page (e.g. accidental
    // back-scroll, or scenes mapped to identical pages). The nonce
    // becomes part of the iframe `key` AND a query param on the file
    // URL so the browser PDF viewer treats the new src as fresh and
    // re-evaluates the `#page=N&zoom=page-width` fragment.
    const [jumpNonce, setJumpNonce] = useState(0);
    const [feedback, setFeedback] = useState(null);
    // feedback shape: { kind: "notfound" | "freetext" | "page-map-missing" | "jumped", message?, page? }

    const isOwner = project?.role === "owner";
    const isAdmin = project?.role === "admin";
    const canManage = isOwner || isAdmin;
    const isTvSeries = project?.production_type === "tv_series";
    // Iter-156C Phase 3 — script access control. Reflects the
    // project-level visibility setting (`everyone` / `owner_admin`).
    // When the script is locked AND the viewer is not Owner/Admin,
    // the PDF preview + Download + Open New Tab buttons are
    // replaced by ScriptLockedBanner. Scene jump search stays
    // available because it operates on operational metadata
    // (scene_number / episode / heading) and never reveals the
    // gated description body.
    const scriptLocked = project?.script_visibility === "owner_admin";
    const canViewScriptContent = !scriptLocked || canManage;

    // ----- Initial load: project + script slots -----
    // Iter-156E — extracted into a reusable loader so focus-revalidation
    // can re-run it. Returns silently on failure (the original error
    // state is only set on first load via the `withSpinner` path).
    const loadProjectAndScripts = useCallback(
        async ({ withSpinner = false } = {}) => {
            if (!projectId) return;
            if (withSpinner) setLoading(true);
            try {
                const [{ data: p }, listRes] = await Promise.all([
                    api.get(`${apiBase}/${projectId}`),
                    api
                        .get(`${apiBase}/${projectId}/scripts`)
                        .catch(() => ({ data: [] })),
                ]);
                setProject(p);
                let list = Array.isArray(listRes.data) ? listRes.data : [];
                if (!list.length) {
                    // Legacy single-script projects pre-date /scripts list.
                    try {
                        const { data: legacy } = await api.get(`${apiBase}/${projectId}/script`,
                        );
                        if (
                            legacy &&
                            (legacy.has_original || legacy.id || legacy.filename)
                        ) {
                            list = [
                                {
                                    id: legacy.id || "legacy",
                                    project_id: projectId,
                                    episode: legacy.episode ?? null,
                                    title:
                                        legacy.title || legacy.filename || "Script",
                                    filename: legacy.filename || null,
                                    mime_type: legacy.mime_type || null,
                                    has_original: Boolean(legacy.has_original),
                                    page_count: legacy.page_count || null,
                                },
                            ];
                        }
                    } catch {
                        // ignore
                    }
                }
                setScripts(list);
                setActiveId((curr) => curr || (list.length ? list[0].id : null));
            } catch (e) {
                if (withSpinner) setError(formatApiError(e));
            } finally {
                if (withSpinner) setLoading(false);
            }
        },
        [apiBase, projectId],
    );

    useEffect(() => {
        loadProjectAndScripts({ withSpinner: true });
    }, [loadProjectAndScripts]);

    // Iter-156E — refetch project + script list whenever the tab
    // regains focus. Picks up out-of-band changes such as an Owner
    // toggling script_visibility on a parallel session — the
    // Contributor's open ScriptPage transitions to the locked
    // banner on next focus without manual refresh.
    useRevalidateOnFocus(() => loadProjectAndScripts({ withSpinner: false }));

    const activeDoc = useMemo(
        () => scripts.find((s) => s.id === activeId) || null,
        [scripts, activeId],
    );

    // Reset search state when switching episodes.
    useEffect(() => {
        setQuery("");
        setPdfPage(0);
        setFeedback(null);
    }, [activeId]);

    // ----- Load scenes for the active episode (for scene→page mapping) -----
    useEffect(() => {
        if (!activeDoc) {
            setScenes([]);
            return;
        }
        const ctrl = new AbortController();
        (async () => {
            try {
                // iter-59 fix: load ALL scenes (draft + confirmed) so
                // search works the moment the script is parsed — users
                // shouldn't have to confirm scenes before the search
                // sees them. The previous `status=confirmed` filter was
                // the root cause of "Scene 5 isn't in this script"
                // when scenes were clearly visible in the Scenes list.
                const { data: allScenes } = await api.get(`${apiBase}/${projectId}/scenes`,
                    { signal: ctrl.signal },
                );
                const ep = activeDoc.episode ?? null;
                // Soften the episode filter: if filtering by episode
                // wipes the list (e.g. legacy single-script project
                // where script has episode=null but scenes do not, or
                // vice versa) fall back to the full list. This was
                // the second cause of false "not found" responses.
                let filtered = allScenes || [];
                if (ep != null) {
                    const epUpper = String(ep).toUpperCase();
                    const epMatched = filtered.filter(
                        (s) => String(s.episode || "").toUpperCase() === epUpper,
                    );
                    if (epMatched.length > 0) {
                        filtered = epMatched;
                    }
                }
                if (process.env.NODE_ENV !== "production") {
                    // eslint-disable-next-line no-console
                    console.debug(
                        "[ScriptPage] loaded scenes",
                        {
                            activeEpisode: ep,
                            totalReturned: (allScenes || []).length,
                            afterEpisodeFilter: filtered.length,
                            sampleNumbers: filtered
                                .slice(0, 10)
                                .map((s) => s.scene_number),
                        },
                    );
                }
                setScenes(filtered);
            } catch (e) {
                if (ctrl.signal.aborted) return;
                setError(formatApiError(e));
            }
        })();
        return () => ctrl.abort();
    }, [activeDoc, apiBase, projectId]);

    const isPdf = Boolean(
        activeDoc?.has_original &&
            (activeDoc?.mime_type || "").toLowerCase().includes("pdf"),
    );

    const fileUrl = useMemo(() => {
        if (!activeDoc?.has_original || !activeId || activeId === "legacy") {
            return null;
        }
        return buildFileUrl(apiBase, projectId, activeId, pdfPage, jumpNonce);
    }, [activeDoc?.has_original, activeId, apiBase, projectId, pdfPage, jumpNonce]);

    // ----- Search action -----
    const performSearch = (e) => {
        e?.preventDefault?.();
        const trimmed = query.trim();
        if (!trimmed) {
            setPdfPage(0);
            setFeedback(null);
            return;
        }
        const ref = parseSceneRef(trimmed);
        if (process.env.NODE_ENV !== "production") {
            // eslint-disable-next-line no-console
            console.debug("[ScriptPage] performSearch", {
                input: trimmed,
                parsed: ref,
                scenesAvailable: scenes.length,
            });
        }
        if (ref) {
            // Scene-number style query → use parsed scene→page mapping.
            const { episode, sceneNumber } = ref;
            const epUpper = episode ? episode.toUpperCase() : null;
            const match = scenes.find((s) => {
                const sn = (s.scene_number || "").toString().toUpperCase();
                if (sn !== sceneNumber) return false;
                if (epUpper) {
                    return ((s.episode || "") + "").toUpperCase() === epUpper;
                }
                return true;
            });
            if (process.env.NODE_ENV !== "production") {
                // eslint-disable-next-line no-console
                console.debug("[ScriptPage] match result", {
                    matched: match ? match.scene_number : null,
                    page_number: match ? match.page_number : null,
                    matchEpisode: match ? match.episode : null,
                });
            }
            if (!match) {
                // Echo the canonical scene reference so it's clear our
                // lookup understood "sc 5" / "scene 5" / "5" the same.
                const canonical = epUpper
                    ? `${epUpper}.${sceneNumber}`
                    : sceneNumber;
                setFeedback({
                    kind: "notfound",
                    message: `Scene ${canonical} not found.`,
                });
                return;
            }
            if (match.page_number && match.page_number > 0) {
                // iter-65 #4: we no longer pretend an embedded iframe
                // jumps. The result card below offers Open Scene as
                // the actionable path; "page Y" is shown for context.
                setPdfPage(match.page_number);
                setJumpNonce((n) => n + 1);
                setFeedback({
                    kind: "jumped",
                    page: match.page_number,
                    sceneId: match.id,
                    sceneNumber: match.scene_number,
                    sceneHeading: match.heading || "",
                    message: `Scene ${match.scene_number}${match.heading ? " — " + match.heading : ""}`,
                });
            } else {
                // Match found but no page map. We jump to page 1 so
                // the user lands inside the document, but surface the
                // "re-parse" hint per spec.
                setPdfPage(1);
                setJumpNonce((n) => n + 1);
                setFeedback({
                    kind: "page-map-missing",
                    sceneId: match.id,
                    sceneNumber: match.scene_number,
                    sceneHeading: match.heading || "",
                    message:
                        "Scene found but no page mapping available — re-run Build Scenes & Cast from Script Setup.",
                });
            }
            return;
        }
        // Free-text / character name → defer to the browser/PDF viewer's
        // native Find. We don't fake an in-app search.
        setFeedback({
            kind: "freetext",
            message:
                "Use the PDF viewer's Find tool (Cmd/Ctrl+F or the search button in the toolbar) to locate text or character names inside the script.",
        });
    };

    const clearSearch = () => {
        setQuery("");
        setPdfPage(0);
        setFeedback(null);
    };

    const showEpisodePicker = isTvSeries && scripts.length > 0;
    const subtitle = showEpisodePicker
        ? "Script · Episodic"
        : "Script";

    return (
        <div data-testid="script-page" className="min-h-screen">
            <ProjectHeader projectName={project?.name} subtitle={subtitle} />

            {/* Iter-118.9 — body container standardised to
                `max-w-2xl mx-auto px-5` so left/right anchors line
                up with every other top-level screen (Overview,
                Scenes, Cast, Schedule, Notes, Settings…). The
                shared ProjectHeader uses the same primitive, so
                the back button + page content share an identical
                X position whether the user is on Script, Flags,
                or anywhere else. */}
            <main className="max-w-2xl mx-auto px-5 pt-5 pb-44 sm:pb-32 w-full">
                <div className="flex items-start gap-3 mb-4">
                    <div className="h-10 w-10 rounded-lg bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                        <FileText size={18} />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h1
                            data-testid="script-page-title"
                            className="font-display text-2xl sm:text-3xl font-black text-white leading-none truncate"
                        >
                            {activeDoc?.title ||
                                activeDoc?.filename ||
                                (showEpisodePicker
                                    ? "Episode script"
                                    : "Script")}
                        </h1>
                        <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-1.5">
                            {activeDoc?.has_original
                                ? `${activeDoc?.page_count || "—"} pages · ${
                                      isPdf ? "original PDF" : (activeDoc?.filename || "file")
                                  }`
                                : "No script uploaded"}
                        </p>
                    </div>
                </div>

                {error ? (
                    <p
                        data-testid="script-page-error"
                        className="text-sm text-red-400 mb-4"
                    >
                        {error}
                    </p>
                ) : null}

                {showEpisodePicker ? (
                    <div
                        data-testid="script-episode-picker"
                        className="mb-3 flex items-center gap-2"
                    >
                        <span className="shrink-0 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                            <Layers size={12} />
                            Episode
                        </span>
                        <div
                            role="tablist"
                            aria-label="Script episodes"
                            className="flex items-center gap-1.5 overflow-x-auto no-scrollbar flex-1 min-w-0 pb-0.5"
                        >
                            {scripts.map((s) => {
                                const active = s.id === activeId;
                                return (
                                    <button
                                        key={s.id}
                                        type="button"
                                        role="tab"
                                        aria-selected={active}
                                        data-active={active ? "true" : "false"}
                                        data-testid={`script-episode-tab-${s.id}`}
                                        onClick={() => setActiveId(s.id)}
                                        // Iter-101 — polished pill style.
                                        // Active uses the primary ring +
                                        // fill so the selected episode is
                                        // unambiguous on dark backgrounds;
                                        // idle uses a subtle hairline so
                                        // the pills still read as a group.
                                        className={[
                                            "tap shrink-0 inline-flex items-center justify-center gap-1 h-9 px-3.5 rounded-full text-[11px] font-semibold uppercase tracking-[0.18em] border transition-colors",
                                            active
                                                ? "bg-primary text-primary-foreground border-primary shadow-sm ring-1 ring-primary/40"
                                                : "bg-transparent text-neutral-300 border-white/10 hover:border-white/25 hover:text-white",
                                        ].join(" ")}
                                    >
                                        Ep {s.episode || "—"}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ) : null}

                {/* iter-80 PART 2 — Layout order: Title → Episode picker
                    → Open/Download (primary actions) → Scene jump search.
                    The script viewer's primary affordance is reading or
                    downloading the document; scene jump search is a
                    secondary navigation aid and now sits below it. */}
                {loading ? (
                    <div
                        data-testid="script-loading"
                        className="h-64 rounded-xl border border-white/10 bg-card animate-pulse mt-4"
                    />
                ) : !activeDoc?.has_original ? (
                    <div
                        data-testid="script-empty"
                        className="rounded-xl border border-dashed border-white/10 p-10 text-center mt-4"
                    >
                        <FileText size={28} className="mx-auto text-neutral-500" />
                        <p className="mt-3 text-sm text-neutral-300">
                            {showEpisodePicker
                                ? "This episode has no script uploaded yet."
                                : "No script uploaded yet."}
                        </p>
                        <p className="text-xs text-neutral-500 mt-1">
                            {canManage ? (() => {
                                // Iter-158.13.2.20e.17 — scope-aware
                                // guidance. On the Route 2 (beta/H&M)
                                // mount, Script is Production-owned
                                // and the canonical Script Setup lives
                                // at `/beta/projects/:pid/script-setup`.
                                // The old "Settings → Script setup"
                                // hint dead-linked into H&M Settings
                                // where Script Setup no longer lives.
                                const link = computeScriptEmptyStateLink({
                                    apiBase, projectId, routeBase,
                                });
                                if (link.isBeta) {
                                    return (
                                        <>
                                            Script is managed at Production scope. Open{" "}
                                            <Link
                                                to={link.to}
                                                data-testid="script-empty-setup-link"
                                                className="underline text-primary hover:text-white"
                                            >
                                                {link.label}
                                            </Link>
                                            {" "}to upload or replace the canonical script for this Production.
                                        </>
                                    );
                                }
                                return (
                                    <>
                                        Upload one in{" "}
                                        <Link
                                            to={link.to}
                                            data-testid="script-empty-settings-link"
                                            className="underline text-primary hover:text-white"
                                        >
                                            {link.label}
                                        </Link>
                                        .
                                    </>
                                );
                            })() : (
                                // Iter-158.13.2.20e.17 — under the
                                // Production/Department model an H&M
                                // Admin is still not authorised to
                                // manage Script; point the reader at
                                // someone with the right Production
                                // authority rather than "the project
                                // owner" specifically.
                                apiBase && String(apiBase).startsWith("/beta/")
                                    ? "Ask the Production Owner or a member with the appropriate Production authority to upload a script."
                                    : "Ask the project owner to upload a script."
                            )}
                        </p>
                    </div>
                ) : !canViewScriptContent ? (
                    /* Iter-156C Phase 3 — Script restricted to Admins.
                       Replaces the PDF preview + Download / Open
                       New Tab affordances with a calm read-only
                       banner. Scene jump (operational metadata)
                       still renders below — see further down the
                       tree. */
                    <ScriptLockedBanner
                        filename={activeDoc?.filename || null}
                    />
                ) : !isPdf && fileUrl ? (
                    /* Non-PDF source (DOCX / TXT / Fountain): we don't
                       try to render a fake document. The user gets the
                       file as-is — they can open it natively. */
                    <div
                        data-testid="script-non-pdf"
                        className="mt-4 rounded-xl border border-white/10 bg-card p-6 text-center"
                    >
                        <FileText size={24} className="mx-auto text-neutral-400" />
                        <p className="mt-3 text-sm text-white">
                            <span className="font-semibold">
                                {activeDoc.filename || "This script"}
                            </span>{" "}
                            isn&rsquo;t a PDF.
                        </p>
                        <p className="text-xs text-neutral-400 mt-1">
                            Open it in a new tab to view the original file.
                            For inline reading, re-upload as a PDF in
                            Settings → Script setup.
                        </p>
                        <a
                            data-testid="script-non-pdf-open"
                            href={fileUrl}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="tap inline-flex items-center gap-2 mt-4 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:opacity-95"
                        >
                            <ExternalLink size={14} />
                            Open file
                        </a>
                    </div>
                ) : fileUrl ? (
                    /* Iter-157L — Unified PDF action surface. The
                       Script PDF actions now use the same shared
                       MobilePdfHandoffCard component as Documents
                       and Schedules so the user sees ONE PDF
                       interaction pattern across the whole app:
                       primary "Open PDF" + secondary "Download".
                       Scene jump still lives below this card (it's
                       a contextual control specific to scripts and
                       is permitted by the iter-157L spec — only the
                       PDF action block itself needs to be
                       consistent). */
                    <MobilePdfHandoffCard
                        fileUrl={fileUrl}
                        filename={activeDoc?.filename || "Script PDF"}
                        downloadHref={fileUrl}
                        subtitle={
                            activeDoc?.page_count
                                ? `${activeDoc.page_count}-page script — opens in your browser's PDF viewer. Use Scene jump below to navigate to a specific scene.`
                                : "Opens in your browser's PDF viewer. Use Scene jump below to navigate to a specific scene."
                        }
                        testidPrefix="script-pdf"
                    />
                ) : null}

                {/* Scene jump — secondary navigation aid. Sits BELOW the
                    primary Open/Download actions per the iter-80 PART 2
                    layout order. Non-sticky to keep the document
                    actions visually anchored to the title.
                    Iter-156C Phase 3 — hidden for non-Admin when the
                    script is locked. Scene jump uses parser-sourced
                    page_number (now null for non-Admin per the
                    backend strip) AND jumps inside the PDF iframe
                    which is also hidden. With no destination
                    and no canvas to jump on, surfacing the
                    affordance would be cargo-cult. */}
                {activeDoc?.has_original && canViewScriptContent ? (
                    <section
                        data-testid="script-search-bar"
                        className="mt-4 rounded-xl border border-white/10 bg-card p-3"
                    >
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">
                            <ArrowRight size={11} />
                            Scene jump
                        </div>
                        <form
                            onSubmit={performSearch}
                            className="flex items-center gap-2"
                        >
                            <div className="relative flex-1">
                                <Search
                                    size={14}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none"
                                />
                                <Input
                                    data-testid="script-search-input"
                                    value={query}
                                    onChange={(e) => {
                                        setQuery(e.target.value);
                                        if (feedback) setFeedback(null);
                                    }}
                                    placeholder="Scene 25, 25A, 1.4"
                                    inputMode="search"
                                    autoComplete="off"
                                    enterKeyHint="search"
                                    className="h-11 pl-9 pr-2 bg-secondary border-transparent text-sm"
                                />
                            </div>
                            <Button
                                type="submit"
                                data-testid="script-search-go"
                                disabled={!query.trim()}
                                className="h-11 px-4 font-semibold tracking-wide"
                            >
                                Go
                                <ArrowRight size={14} className="ml-1.5" />
                            </Button>
                            {pdfPage > 0 || feedback ? (
                                <button
                                    type="button"
                                    data-testid="script-search-clear"
                                    onClick={clearSearch}
                                    aria-label="Clear search"
                                    className="tap h-11 w-9 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-white hover:bg-white/5"
                                >
                                    ×
                                </button>
                            ) : null}
                        </form>

                        {feedback?.kind === "notfound" ? (
                            <div
                                data-testid="script-search-notfound"
                                className="mt-2 flex items-start gap-2 rounded-md border border-amber-400/30 bg-amber-400/10 text-amber-100 text-xs px-3 py-2"
                            >
                                <AlertCircle
                                    size={14}
                                    className="shrink-0 mt-0.5"
                                />
                                <span>{feedback.message}</span>
                            </div>
                        ) : null}
                        {feedback?.kind === "page-map-missing" ? (
                            <div
                                data-testid="script-page-map-missing"
                                className="mt-2 flex items-start gap-2 rounded-md border border-amber-400/30 bg-amber-400/10 text-amber-100 text-xs px-3 py-2"
                            >
                                <AlertCircle
                                    size={14}
                                    className="shrink-0 mt-0.5"
                                />
                                <span>{feedback.message}</span>
                            </div>
                        ) : null}
                        {feedback?.kind === "freetext" ? (
                            <div
                                data-testid="script-search-freetext-hint"
                                className="mt-2 flex items-start gap-2 rounded-md border border-white/10 bg-secondary/50 text-neutral-200 text-xs px-3 py-2"
                            >
                                <Info
                                    size={14}
                                    className="shrink-0 mt-0.5 text-primary"
                                />
                                <span>{feedback.message}</span>
                            </div>
                        ) : null}
                        {!feedback && isPdf ? (
                            <p
                                data-testid="script-search-find-hint"
                                className="mt-2 text-[11px] text-neutral-500 leading-relaxed"
                            >
                                Tip: type a scene number to jump to its
                                page. To find a character name or any
                                text, use the PDF viewer&rsquo;s Find tool
                                (<kbd className="px-1 py-0.5 rounded bg-secondary text-neutral-300 border border-white/10 font-mono text-[10px]">⌘F</kbd>{" "}
                                /{" "}
                                <kbd className="px-1 py-0.5 rounded bg-secondary text-neutral-300 border border-white/10 font-mono text-[10px]">Ctrl-F</kbd>
                                , or the magnifier icon on iOS).
                            </p>
                        ) : null}
                        {feedback?.kind === "jumped" ? (
                            <div
                                data-testid="script-search-result-card"
                                className="mt-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3"
                            >
                                <p
                                    data-testid="script-search-result-meta"
                                    className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-semibold"
                                >
                                    Match found
                                </p>
                                <p
                                    data-testid="script-search-page-indicator"
                                    className="text-sm text-white font-semibold mt-0.5"
                                >
                                    {feedback.message}{" "}
                                    {feedback.page ? (
                                        <span className="text-neutral-500 text-[11px] font-normal">
                                            (page {feedback.page})
                                        </span>
                                    ) : null}
                                </p>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {feedback.sceneId ? (
                                        <Link
                                            data-testid="script-search-open-scene"
                                            to={`${routeBase}/${projectId}/scenes/${feedback.sceneId}`}
                                            className="tap inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3 h-9 text-[12px] font-semibold"
                                        >
                                            Open Scene
                                            <ArrowRight size={12} />
                                        </Link>
                                    ) : null}
                                </div>
                            </div>
                        ) : null}
                        {feedback?.kind === "page-map-missing" &&
                        feedback?.sceneId ? (
                            <div className="mt-2 flex flex-wrap gap-2">
                                <Link
                                    data-testid="script-search-open-scene-fallback"
                                    to={`${routeBase}/${projectId}/scenes/${feedback.sceneId}`}
                                    className="tap inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-[12px] font-semibold"
                                >
                                    Open scene {feedback.sceneNumber}
                                    <ArrowRight size={12} />
                                </Link>
                            </div>
                        ) : null}
                    </section>
                ) : null}
                {/* Iter-158.13.2.20f.34 — H&M AI Breakdown V1 panel.
                    Sits directly beneath the Script/PDF surface. Read
                    gate = existing canViewScriptContent; Generate gate
                    on the FE approximates via canManage (Owner/Admin);
                    the backend applies the spec-accurate
                    `can_view_script_content AND can_contribute`
                    composed predicate, so H&M Contributors receive a
                    clean 403 without the FE inventing a new
                    permission model. Mock adapter only in V1 — no
                    Script content leaves OnSetFlow. */}
                {activeDoc?.id ? (
                    <HMBreakdownPanel
                        projectId={projectId}
                        scriptId={activeDoc.id}
                        canGenerate={canManage && canViewScriptContent}
                        canView={canViewScriptContent}
                    />
                ) : null}
            </main>
        </div>
    );
}
